import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import './StyledInput.css';
import './DateInput.css';
import {
  MIN_DATE, MAX_DATE, formatDate, parseDateText, getTenantDateFormat, todayInTenant, todayAsLocalDate,
} from './tenantClock';

// The one date field for the whole app (ui-architecture.md "Dates"). A text
// box in the company Date Format (Company Settings > Localisation) plus a
// calendar dropdown, so it looks and reads the same in every browser
// instead of following the viewer's browser language the way a native
// <input type="date"> does. Only a real date with a 4-digit year can be
// entered.
//
// Same props as <input type="date">: value/defaultValue and onChange stay
// YYYY-MM-DD ('' for blank), and onChange/onBlur get an event whose
// target.value is that YYYY-MM-DD. onChange fires as soon as the typed text
// is a complete date (2-digit day and month, 4-digit year) or a calendar
// day is picked — never on a half-typed date, so a field that saves on
// change (Quote Date, Order Date) doesn't save 2/12/2026 while someone is
// retyping 26/12/2026. On blur a looser entry (1/2/2026) is accepted, and
// anything that still isn't a date goes back to the last good value.
// `min`/`max` (YYYY-MM-DD) cap the range; the year never runs past 9999.

// Layout props that size the field as a whole rather than the text inside
// it, so `style={{ width: 140 }}` still means "140px wide" with the
// calendar button in there.
const WRAPPER_STYLE_KEYS = [
  'width', 'minWidth', 'maxWidth', 'flex', 'flexGrow', 'flexShrink', 'flexBasis', 'alignSelf',
  'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft', 'display',
];
const DEFAULT_WIDTH = 140;
const POPOVER_GAP = 4;

function toLocalDate(ymd) {
  if (!ymd) return undefined;
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function fromLocalDate(date) {
  return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// Put the Date Format's separator in as each part fills while someone types
// forward, so 01012011 shows as 01/01/2011. A separator typed early (1/2/2026)
// is kept, any of / - . or space becomes the format's own, and digits past a
// 4-digit year are dropped.
function autoSeparate(text) {
  const format = getTenantDateFormat();
  const sep = format === 'yyyy-mm-dd' ? '-' : '/';
  const lengths = format === 'yyyy-mm-dd' ? [4, 2, 2] : [2, 2, 4];
  let out = '';
  let part = 0;
  let count = 0;
  for (const ch of text) {
    if (/\d/.test(ch)) {
      if (count === lengths[part]) {
        if (part === 2) break;
        out += sep;
        part += 1;
        count = 0;
      }
      out += ch;
      count += 1;
    } else if (/[/\-.\s]/.test(ch) && count > 0 && part < 2) {
      out += sep;
      part += 1;
      count = 0;
    }
  }
  if (part < 2 && count === lengths[part]) out += sep;
  return out;
}

function dateEvent(value, name, id) {
  const target = { value, name, id };
  return { target, currentTarget: target, preventDefault() {}, stopPropagation() {} };
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M1.5 6h13M5 1v3M11 1v3" stroke="currentColor" strokeWidth="1.3" fill="none" />
    </svg>
  );
}

const DateInput = forwardRef(function DateInput({
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  min,
  max,
  disabled,
  className = 'styled-input',
  style,
  name,
  id,
  placeholder,
  ...rest
}, ref) {
  const isControlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(defaultValue || '');
  const current = (isControlled ? value : innerValue) || '';
  const lower = min && min > MIN_DATE ? min : MIN_DATE;
  const upper = max && max < MAX_DATE ? max : MAX_DATE;

  const [text, setText] = useState(() => formatDate(current));
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  const setRefs = useCallback((node) => {
    inputRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  // Show the stored value whenever nobody is typing — a parent resetting
  // the field, or a blur reverting a half-typed date.
  useEffect(() => {
    if (!focused) setText(formatDate(current));
  }, [current, focused]);

  const inRange = (ymd) => ymd >= lower && ymd <= upper;

  const commit = (ymd) => {
    if (ymd === current) return;
    if (!isControlled) setInnerValue(ymd);
    if (onChange) onChange(dateEvent(ymd, name, id));
  };

  const handleTextChange = (e) => {
    let next = e.target.value;
    // Only when adding at the end: backspacing or editing mid-text is left
    // alone so the caret doesn't jump.
    if (next.length > text.length && e.target.selectionStart === next.length) next = autoSeparate(next);
    setText(next);
    if (next.trim() === '') return;
    const ymd = parseDateText(next, { strict: true });
    if (ymd && inRange(ymd)) commit(ymd);
  };

  // Focus has left the whole control (text box and calendar): settle the
  // typed text into a value, then tell the caller.
  const finish = () => {
    setOpen(false);
    setFocused(false);
    let settled = current;
    if (text.trim() === '') {
      settled = '';
    } else {
      const ymd = parseDateText(text);
      if (ymd && inRange(ymd)) settled = ymd;
    }
    commit(settled);
    setText(formatDate(settled));
    if (onBlur) onBlur(dateEvent(settled, name, id));
  };

  const isInside = (node) => !!node && (wrapperRef.current?.contains(node) || popoverRef.current?.contains(node));

  const handleFocusOut = (e) => {
    if (isInside(e.relatedTarget)) return;
    finish();
  };

  const handleFocus = (e) => {
    if (!focused) {
      setFocused(true);
      if (onFocus) onFocus(e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' && (e.altKey || !open)) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      return;
    }
    if (e.key === 'Enter') {
      // Settle a loosely typed date before a form's own Enter handling.
      const ymd = parseDateText(text);
      if (ymd && inRange(ymd)) {
        commit(ymd);
        setText(formatDate(ymd));
      }
      setOpen(false);
    }
    if (onKeyDown) onKeyDown(e);
  };

  const pick = (date) => {
    if (!date) return;
    const ymd = fromLocalDate(date);
    commit(ymd);
    setText(formatDate(ymd));
    setOpen(false);
    inputRef.current?.focus();
  };

  // The calendar sits in a portal so a table cell or drawer body with
  // overflow hidden can't clip it; it follows the field on scroll/resize.
  const placePopover = useCallback(() => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const height = popoverRef.current?.offsetHeight || 340;
    const width = popoverRef.current?.offsetWidth || 300;
    const below = rect.bottom + POPOVER_GAP;
    const top = below + height > window.innerHeight && rect.top - POPOVER_GAP - height > 0
      ? rect.top - POPOVER_GAP - height
      : below;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    // A dialog centred with a CSS transform (ConfirmDialog) becomes the
    // reference box for position: fixed, so measure from it instead of
    // the window.
    const host = popoverRef.current?.parentElement;
    if (host && host !== document.body && getComputedStyle(host).transform !== 'none') {
      const hostRect = host.getBoundingClientRect();
      setPopoverPos({
        top: top - hostRect.top - host.clientTop + host.scrollTop,
        left: left - hostRect.left - host.clientLeft + host.scrollLeft,
      });
      return;
    }
    setPopoverPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    placePopover();
    window.addEventListener('scroll', placePopover, true);
    window.addEventListener('resize', placePopover);
    return () => {
      window.removeEventListener('scroll', placePopover, true);
      window.removeEventListener('resize', placePopover);
    };
  }, [open, placePopover]);

  // A click anywhere else closes the calendar; the blur that click causes
  // settles the value. (React bubbles the calendar's own focus events up
  // through the portal to the wrapper's onBlur.)
  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (e) => {
      if (!wrapperRef.current?.contains(e.target) && !popoverRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const wrapperStyle = { width: DEFAULT_WIDTH };
  const inputStyle = {};
  Object.entries(style || {}).forEach(([key, v]) => {
    if (WRAPPER_STYLE_KEYS.includes(key)) wrapperStyle[key] = v;
    else inputStyle[key] = v;
  });
  if (wrapperStyle.display === 'block') wrapperStyle.display = 'flex';

  // Red only once the entry is long enough to be a whole date, so it
  // doesn't flash while someone is still typing.
  const loose = parseDateText(text);
  const invalid = focused && text.replace(/\s/g, '').length >= 8 && !(loose && inRange(loose));

  const selected = toLocalDate(current);
  const startMonth = toLocalDate(lower > '1900-01-01' ? lower : '1900-01-01');
  const endMonth = toLocalDate(upper < '2100-12-31' ? upper : '2100-12-31');
  const disabledDays = [];
  if (min) disabledDays.push({ before: toLocalDate(lower) });
  if (max && max < MAX_DATE) disabledDays.push({ after: toLocalDate(upper) });
  const today = todayInTenant();
  const todayAllowed = inRange(today);

  return (
    <span
      ref={wrapperRef}
      className={`date-input${disabled ? ' date-input--disabled' : ''}`}
      style={wrapperStyle}
      onBlur={handleFocusOut}
    >
      <input
        {...rest}
        ref={setRefs}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        // Every Date Format is exactly 10 characters, so a year can't run
        // past 4 digits as it's typed.
        maxLength={10}
        id={id}
        name={name}
        className={`${className} date-input__text${invalid ? ' date-input__text--invalid' : ''}`}
        style={inputStyle}
        value={text}
        placeholder={placeholder || getTenantDateFormat()}
        disabled={disabled}
        onChange={handleTextChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        aria-invalid={invalid || undefined}
        title={invalid ? `Enter a real date as ${getTenantDateFormat()}` : rest.title}
      />
      <button
        type="button"
        className="date-input__button"
        tabIndex={-1}
        disabled={disabled}
        aria-label="Choose date"
        title="Choose date"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          if (!focused) inputRef.current?.focus();
          setOpen((o) => !o);
        }}
      >
        <CalendarIcon />
      </button>
      {/* Inside a modal (Radix Dialog, e.g. FilterDrawer) the calendar
          goes into the dialog itself: Radix blocks pointer events on
          everything outside it, so a calendar in document.body could be
          seen but not clicked. */}
      {open && createPortal(
        <div
          ref={popoverRef}
          className="date-input__popover"
          style={{ top: popoverPos?.top ?? -9999, left: popoverPos?.left ?? -9999 }}
          onClick={(e) => e.stopPropagation()}
          // Keep focus in the text box while clicking days, so a click
          // never reads as leaving the field. The month/year dropdowns
          // need their own mousedown to open.
          onMouseDown={(e) => { if (e.target.tagName !== 'SELECT') e.preventDefault(); }}
        >
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={pick}
            defaultMonth={selected || todayAsLocalDate()}
            captionLayout="dropdown"
            startMonth={startMonth}
            endMonth={endMonth}
            disabled={disabledDays}
            today={todayAsLocalDate()}
            weekStartsOn={getTenantDateFormat() === 'mm/dd/yyyy' ? 0 : 1}
            showOutsideDays
          />
          <div className="date-input__footer">
            <button type="button" className="date-input__link" disabled={!todayAllowed} onClick={() => pick(toLocalDate(today))}>Today</button>
            <button
              type="button"
              className="date-input__link"
              onClick={() => { commit(''); setText(''); setOpen(false); inputRef.current?.focus(); }}
            >
              Clear
            </button>
          </div>
        </div>,
        wrapperRef.current?.closest('[role="dialog"]') || document.body,
      )}
    </span>
  );
});

export default DateInput;
